<div [ngClass]="{ wrap: wrapContent }">
  <div class="shots-print-header print-header">
    <!-- Header -->
    @if (showLabel) {
      <div class="header">
        <div class="title">
          <span [innerHTML]="labelText | tokenToDiv"></span>
        </div>
        <span class="message">* populated from PatientNow</span>
      </div>
    }

    <!-- Description -->
    @if (contentText) {
      <div class="flex flex-row">
        <div class="flex word-wrap mb10">
          <div class="flex subtitle">
            <span [innerHTML]="contentText | tokenToDiv"></span>
          </div>
        </div>
      </div>
    }

    <mat-divider></mat-divider>
  </div>

  @if (isFormLoaded | async) {
    <!-- Shots Content -->
    @let fields = shotFields;
    @let visibleEntries = getVisibleShotEntries();

    <div [formGroup]="form" class="shots-container" [class.shots-container--print]="isPrinting">
      @if (!isWidgetHidden && (!isPrinting || visibleEntries.length > 0)) {
        <div formArrayName="items" class="shots-list">
          @for (entry of visibleEntries; track entry.index; let isFirstEntry = $first) {
            @let shotGroup = entry.group;
            @let i = entry.index;
            <div [formGroupName]="i" class="shot-item" [class.shot-item--first]="isFirstEntry">
              @for (field of fields; track $index; let fieldIndex = $index) {
                <ng-container
                  [ngTemplateOutlet]="fieldRenderer"
                  [ngTemplateOutletContext]="{
                    shotGroup: shotGroup,
                    field: field,
                    shotIndex: i,
                    controlId: controlId(field.name, i, fieldIndex),
                  }"
                ></ng-container>
              }
              @if (
                !isPrinting &&
                ((shotsFormArray.length === 1 && !shotGroup.get('isManuallyAdded')?.value) ||
                  (shotsFormArray.length > 1 && shotGroup.get('isManuallyAdded')?.value))
              ) {
                <button
                  type="button"
                  class="remove-shot-btn"
                  [disabled]="!shouldInitializeDate"
                  (click)="removeShot(i)"
                >
                  <rx-icon name="icon-close"></rx-icon>
                </button>
              }
            </div>
          }
        </div>
      }

      <!-- Add Shot -->
      @if (!isPrinting && !isEmrReadonly) {
        <button type="button" class="add-shot-btn" [disabled]="!shouldInitializeDate" (click)="addShot()">
          <span class="add-shot-icon">
            <rx-icon name="icon-circle-plus-2" [size]="16"></rx-icon>
          </span>
          <span>Add Shot</span>
        </button>
      }

      @if (isEmrReadonly) {
        <fb-emr-readonly-notice />
      }
    </div>
  } @else {
    <div class="shots-spinner"><mat-spinner class="loading-spinner" diameter="40"></mat-spinner></div>
  }

  <!-- Footer hints -->
  @if (hintText) {
    <div class="hint" [innerHTML]="hintText | tokenToDiv"></div>
  }

  @if (isRequired && isSubmitted && !isValid()) {
    <div class="hint-required">This field is required</div>
  }
</div>

<ng-template
  #fieldRenderer
  let-shotGroup="shotGroup"
  let-field="field"
  let-shotIndex="shotIndex"
  let-controlId="controlId"
>
  <ng-container [formGroup]="shotGroup">
    @let control = shotGroup.get(field.name);
    @let isInvalid = isSubmitted && control?.invalid;

    <div class="shot-field" [ngClass]="{ 'full-row': field?.fullRow }">
      <label class="shot-label" [attr.for]="controlId">
        <span class="shot-label-content">
          <span>{{ field.label }}</span>
          @if (isFieldRequired(shotGroup, field.name)) {
            <span class="required-mark">*</span>
          }
          <fb-scribe-suggestion-indicator
            [target]="getShotMarkerTarget(shotIndex)"
            [fieldKey]="field.name"
          ></fb-scribe-suggestion-indicator>
          <fb-amendment-field-indicator [rowIndex]="shotIndex" [fieldKey]="field.name" />
        </span>
      </label>
      @switch (field?.type) {
        @case ('date') {
          <div class="date-input-wrapper">
            <input
              dateInputGuard
              class="shot-input"
              maxlength="10"
              [id]="controlId"
              [formControlName]="field.name"
              [matDatepicker]="datepicker"
              [placeholder]="placeholderFor(field)"
              [readonly]="isEmrReadonly"
              [ngClass]="{ 'is-invalid': isInvalid }"
              (focus)="!isEmrReadonly && datepicker.open()"
              [class.emr-readonly]="isEmrReadonly"
              (input)="clearShotMarker(shotIndex, field.name)"
              (dateChange)="clearShotMarker(shotIndex, field.name)"
            />
            <button
              type="button"
              class="date-picker-btn"
              [disabled]="!shouldInitializeDate"
              (click)="!isEmrReadonly && datepicker.open()"
            >
              <rx-icon name="icon-calendar2"></rx-icon>
            </button>
            <mat-datepicker #datepicker></mat-datepicker>
          </div>
        }

        @case ('autocomplete') {
          @if (isPrinting || isEmrReadonly) {
            <input
              class="shot-input"
              type="text"
              [id]="controlId"
              [readonly]="true"
              [value]="getPrintDisplayValue(shotGroup, field)"
              [class.emr-readonly]="isEmrReadonly"
            />
          } @else if (field.name === 'employee_id') {
            <ng-select
              class="employee-select"
              dropdownPosition="auto"
              appendTo="body"
              bindLabel="text"
              bindValue="value"
              [id]="controlId"
              [formControlName]="field.name"
              [items]="employees$ | async"
              [searchFn]="customSearchFn"
              [loading]="isLoadingEmployees()"
              [placeholder]="placeholderFor(field)"
              [searchable]="true"
              [virtualScroll]="true"
              [clearable]="true"
              [closeOnSelect]="true"
              [ngClass]="{ 'is-invalid': isInvalid }"
              (scrollToEnd)="onScrollToEnd()"
              (search)="onSearch($event)"
              (change)="clearShotMarker(shotIndex, field.name)"
            >
              <ng-template ng-option-tmp let-item="item">{{ item.text }}</ng-template>
            </ng-select>
          }
        }

        @case ('select') {
          @if (isPrinting || isEmrReadonly) {
            <input
              class="shot-input"
              type="text"
              [id]="controlId"
              [readonly]="true"
              [value]="getPrintDisplayValue(shotGroup, field)"
              [class.emr-readonly]="isEmrReadonly"
            />
          } @else if (!['company_code', 'state', 'ph_state'].includes(field.name)) {
            <ng-select
              [ngClass]="{
                'has-conflict': field.hasConflict && mode !== 'kiosk' && !isPreview,
                'is-invalid': isInvalid,
              }"
              [id]="controlId"
              [formControlName]="field.name"
              [items]="(selectFilterOptionsMap[field.name]?.items | async) || []"
              [bindLabel]="field?.optionLabelKey"
              [bindValue]="field?.optionValueKey"
              [closeOnSelect]="true"
              [searchable]="false"
              [virtualScroll]="field.name === 'compound'"
              [clearable]="true"
              [loading]="selectFilterOptionsMap[field.name]?.isLoading | async"
              [placeholder]="placeholderFor(field)"
              (scrollToEnd)="onSelectScrollToEnd(field.name)"
              class="shot-select"
              (change)="clearShotMarker(shotIndex, field.name)"
            ></ng-select>
          }
        }

        @case ('textarea') {
          <textarea
            appPasteStrip
            appTextareaAutosize
            [appTextareaAutosizeUnlimited]="isPrinting"
            class="shot-textarea"
            [class.shot-textarea--print]="isPrinting"
            [id]="controlId"
            [formControlName]="field.name"
            [placeholder]="field?.placeholder"
            [ngClass]="{ 'is-invalid': isInvalid }"
            [class.emr-readonly]="isEmrReadonly"
            (input)="clearShotMarker(shotIndex, field.name)"
          ></textarea>
        }

        @case ('text') {
          <input
            class="shot-input"
            type="text"
            [id]="controlId"
            [formControlName]="field.name"
            [placeholder]="placeholderFor(field)"
            [ngClass]="{ 'is-invalid': isInvalid }"
            [class.emr-readonly]="isEmrReadonly"
            (input)="clearShotMarker(shotIndex, field.name)"
          />
        }

        @case ('number') {
          <input
            decimalDisplay
            class="shot-input"
            type="text"
            [id]="controlId"
            [formControlName]="field.name"
            [decimalFormat]="decimalFormat"
            [decimalLocale]="decimalLocale"
            [ngClass]="{ 'is-invalid': isInvalid }"
            [class.emr-readonly]="isEmrReadonly"
            (input)="clearShotMarker(shotIndex, field.name)"
          />
        }

        @default {
          <div class="shot-field">
            <div>Unsupported field type: {{ field?.type }}</div>
          </div>
        }
      }

      @let error = getFieldErrorByControl(control, field.label);

      @if (error) {
        <div class="error-message">{{ error }}</div>
      }
    </div>
  </ng-container>
</ng-template>
